import api from './api';

// Authentifizierter Datei-Abruf: liefert Blob + Dateiname aus Content-Disposition;
// Fehler enthalten die Server-Meldung (detail).
const authFetchBlob = async (path, fallbackError) => {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const response = await fetch(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    let detail = '';
    try { detail = (await response.json())?.detail || ''; } catch (_e) { /* kein JSON */ }
    throw new Error(detail || fallbackError);
  }
  const cd = response.headers.get('Content-Disposition') || '';
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = cd.match(/filename="?([^";]+)"?/i);
  let filename = '';
  try { filename = star ? decodeURIComponent(star[1]) : (plain ? plain[1] : ''); } catch (_e) { filename = plain ? plain[1] : ''; }
  return { data: await response.blob(), filename };
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 0);
};

const emailService = {
  // E-Mail-Status prüfen
  getStatus: async () => {
    const response = await api.get('/email/status');
    return response.data;
  },

  // E-Mail-Konten abrufen
  getEmailAccounts: async () => {
    const response = await api.get('/email/accounts');
    return response.data;
  },

  // Posteingang abrufen (mit optionaler serverseitiger Volltextsuche)
  getInbox: async (folder = 'INBOX', limit = 50, offset = 0, accountId = null, search = null) => {
    const params = { folder, limit, offset };
    if (accountId) params.account_id = accountId;
    if (search && search.trim()) params.search = search.trim();
    const response = await api.get('/email/inbox', { params });
    return response.data;
  },

  // Einzelne E-Mail abrufen
  getEmailDetail: async (emailId, folder = 'INBOX', accountId = null) => {
    const params = { folder };
    if (accountId) params.account_id = accountId;
    const response = await api.get(`/email/inbox/${emailId}`, { params });
    return response.data;
  },

  // Ungelesene E-Mails zählen
  getUnreadCount: async (folder = 'INBOX', accountId = null) => {
    const params = { folder };
    if (accountId) params.account_id = accountId;
    const response = await api.get('/email/unread-count', { params });
    return response.data;
  },

  // Ungelesene E-Mails pro Account zählen (lazy load)
  getAccountsUnreadCounts: async () => {
    const response = await api.get('/email/accounts/unread-counts');
    return response.data;
  },

  // Batch-Aktionen
  batchMarkRead: async (emailIds, folder = 'INBOX', accountId = null) => {
    const response = await api.post('/email/batch/mark-read', {
      email_ids: emailIds, folder, account_id: accountId
    });
    return response.data;
  },
  batchArchive: async (emailIds, folder = 'INBOX', accountId = null) => {
    const response = await api.post('/email/batch/archive', {
      email_ids: emailIds, folder, account_id: accountId
    });
    return response.data;
  },
  batchDelete: async (emailIds, folder = 'INBOX', accountId = null) => {
    const response = await api.post('/email/batch/delete', {
      email_ids: emailIds, folder, account_id: accountId
    });
    return response.data;
  },

  // E-Mail-Vorlagen
  getEmailTemplates: async () => {
    const response = await api.get('/email/templates');
    return response.data;
  },
  createEmailTemplate: async (template) => {
    const response = await api.post('/email/templates', template);
    return response.data;
  },
  updateEmailTemplate: async (id, template) => {
    const response = await api.put(`/email/templates/${id}`, template);
    return response.data;
  },
  deleteEmailTemplate: async (id) => {
    const response = await api.delete(`/email/templates/${id}`);
    return response.data;
  },

  // IMAP-Ordner abrufen
  getFolders: async () => {
    const response = await api.get('/email/folders');
    return response.data;
  },

  // E-Mail zuordnen
  assignEmail: async (data) => {
    const response = await api.post('/email/assign', data);
    return response.data;
  },

  // Zugeordnete E-Mails abrufen
  getAssignments: async (addressId = null, projectId = null) => {
    const params = {};
    if (addressId) params.address_id = addressId;
    if (projectId) params.project_id = projectId;
    
    const response = await api.get('/email/assignments', { params });
    return response.data;
  },

  // Bulk-Zuordnungs-Status für mehrere E-Mails abrufen
  getBulkAssignments: async (emailIds) => {
    const response = await api.post('/email/assignments/bulk', emailIds);
    return response.data;
  },

  // Einzelne E-Mail-Zuordnung mit Details abrufen
  getEmailAssignment: async (emailId) => {
    const response = await api.get(`/email/assignments/${emailId}`);
    return response.data;
  },

  // Zuordnung löschen
  deleteAssignment: async (assignmentId) => {
    const response = await api.delete(`/email/assignments/${assignmentId}`);
    return response.data;
  },

  // Vorschläge für Zuordnung basierend auf E-Mail-Adresse und Betreff
  getSuggestedAssignment: async (fromEmail, subject, fromName = '') => {
    const response = await api.get('/email/suggest-assignment', {
      params: { from_email: fromEmail || '', subject: subject || '', from_name: fromName || '' }
    });
    return response.data;
  },

  // Adresssuche für den Zuordnen-Dialog (alle Adressen inkl. Ansprechpartner)
  searchAssignAddresses: async (q) => {
    const response = await api.get('/email/address-search', { params: { q } });
    return response.data?.results || [];
  },

  // Zuordnung löschen
  deleteAssignment: async (assignmentId) => {
    const response = await api.delete(`/email/assignments/${assignmentId}`);
    return response.data;
  },

  // E-Mail archivieren
  archiveEmail: async (emailId, folder = 'INBOX', accountId = null) => {
    const response = await api.post(`/email/archive/${emailId}`, null, {
      params: { folder, account_id: accountId }
    });
    return response.data;
  },

  deleteEmail: async (emailId, folder = 'INBOX', accountId = null) => {
    const response = await api.post(`/email/delete/${emailId}`, null, {
      params: { folder, account_id: accountId }
    });
    return response.data;
  },

  // E-Mail als gelesen markieren
  markAsRead: async (emailId, folder = 'INBOX', accountId = null) => {
    const params = { folder };
    if (accountId) params.account_id = accountId;
    const response = await api.post(`/email/mark-read/${emailId}`, null, { params });
    return response.data;
  },

  // E-Mail als ungelesen markieren
  markAsUnread: async (emailId, folder = 'INBOX', accountId = null) => {
    const params = { folder };
    if (accountId) params.account_id = accountId;
    const response = await api.post(`/email/mark-unread/${emailId}`, null, { params });
    return response.data;
  },

  // E-Mail als Favorit markieren bzw. Markierung entfernen (IMAP-Flag \Flagged)
  setFlagged: async (emailId, flagged = true, folder = 'INBOX', accountId = null) => {
    const params = { folder, flagged };
    if (accountId) params.account_id = accountId;
    const response = await api.post(`/email/flag/${emailId}`, null, { params });
    return response.data;
  },

  // E-Mail an einen Kontakt senden
  sendEmail: async (data) => {
    const response = await api.post('/email/send', data);
    return response.data;
  },

  // E-Mail an eine Adresse senden
  sendToAddress: async (addressId, subject, message) => {
    const response = await api.post(`/email/send-to-address/${addressId}`, null, {
      params: { subject, message }
    });
    return response.data;
  },

  // Massen-E-Mail senden
  sendBulk: async (recipientIds, subject, message) => {
    const response = await api.post('/email/send-bulk', {
      recipient_ids: recipientIds,
      subject,
      message
    });
    return response.data;
  },

  // Terminerinnerung senden
  sendAppointmentReminder: async (appointmentId) => {
    const response = await api.post(`/email/appointment-reminder/${appointmentId}`);
    return response.data;
  },

  // E-Mail-Logs abrufen
  getLogs: async (limit = 50) => {
    const response = await api.get('/email/logs', { params: { limit } });
    return response.data;
  },

  // Anhang zu Adresse/Projekt speichern
  saveAttachment: async (emailId, attachmentIndex, addressId = null, projectId = null, folder = 'INBOX', accountId = null) => {
    const params = { folder };
    if (accountId) params.account_id = accountId;
    const response = await api.post(`/email/inbox/${emailId}/attachments/${attachmentIndex}/save`, {
      address_id: addressId,
      project_id: projectId
    }, { params });
    return response.data;
  },

  // Anhang als Beleg in Buchhaltung (Zu prüfen) ablegen
  saveAttachmentAsBeleg: async (emailId, attachmentIndex, folder = 'INBOX', jahr = null, accountId = null) => {
    const params = { folder };
    if (jahr) params.jahr = jahr;
    if (accountId) params.account_id = accountId;
    const response = await api.post(`/email/inbox/${emailId}/attachments/${attachmentIndex}/save-as-beleg`, {}, { params });
    return response.data;
  },

  // KI-Antwortvorschläge generieren
  suggestReply: async (emailId, emailSubject, emailFrom, emailBody, addressId = null, projectId = null, tone = 'professional') => {
    const response = await api.post('/email/ai/suggest-reply', {
      email_id: emailId,
      email_subject: emailSubject,
      email_from: emailFrom,
      email_body: emailBody,
      address_id: addressId,
      project_id: projectId,
      tone: tone
    });
    return response.data;
  },

  // KI-E-Mail-Agent - Vollständige Analyse
  analyzeEmail: async (emailId, emailSubject, emailFrom, emailFromName, emailBody, emailDate) => {
    const response = await api.post('/email/agent/analyze', {
      email_id: emailId,
      email_subject: emailSubject,
      email_from: emailFrom,
      email_from_name: emailFromName,
      email_body: emailBody,
      email_date: emailDate
    });
    return response.data;
  },

  // Anhang herunterladen (aus dem gewählten Konto/Ordner, mit Original-Dateinamen)
  downloadAttachment: async (emailId, attachmentIndex, folder = 'INBOX', accountId = null, filename = '') => {
    const params = new URLSearchParams({ folder: folder || 'INBOX' });
    if (accountId) params.set('account_id', accountId);
    const blob = await authFetchBlob(`/api/email/inbox/${emailId}/attachments/${attachmentIndex}/download?${params}`, 'Download fehlgeschlagen');
    saveBlob(blob.data, filename || blob.filename || `anhang-${attachmentIndex + 1}`);
    return { success: true };
  },

  // E-Mail als PDF exportieren
  exportEmailAsPdf: async (emailId, folder = 'INBOX', accountId = null) => {
    const params = new URLSearchParams({ folder: folder || 'INBOX' });
    if (accountId) params.set('account_id', accountId);
    const blob = await authFetchBlob(`/api/email/inbox/${emailId}/pdf?${params}`, 'PDF-Export fehlgeschlagen');
    saveBlob(blob.data, blob.filename || `email_${emailId}.pdf`);
    return { success: true };
  },

  // E-Mail-Kategorien
  getCategories: async () => {
    const response = await api.get('/email/categories');
    return response.data;
  },

  createCategory: async (name, color, icon = null) => {
    const response = await api.post('/email/categories', { name, color, icon });
    return response.data;
  },

  updateCategory: async (categoryId, data) => {
    const response = await api.put(`/email/categories/${categoryId}`, data);
    return response.data;
  },

  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/email/categories/${categoryId}`);
    return response.data;
  },

  assignCategory: async (emailId, messageId, categoryId) => {
    const response = await api.post('/email/categories/assign', {
      email_id: emailId,
      message_id: messageId,
      category_id: categoryId
    });
    return response.data;
  },

  removeCategory: async (emailId, categoryId) => {
    const response = await api.delete(`/email/categories/assign/${emailId}/${categoryId}`);
    return response.data;
  },

  getEmailCategories: async (emailId) => {
    const response = await api.get(`/email/categories/email/${emailId}`);
    return response.data;
  },

  getBulkEmailCategories: async (emailIds) => {
    const response = await api.post('/email/categories/bulk', emailIds);
    return response.data;
  },
};
export default emailService;
